import React, { memo, useMemo } from 'react';
import { ScrollView, TouchableOpacity, Text, View, StyleSheet } from 'react-native';
import type { CorkBoardTag } from '@/types/residents/corkBoard';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

interface NewsfeedTagFilterProps {
  tags: CorkBoardTag[];
  selectedTagIds: number[];
  onToggleTag: (tagId: number) => void;
  onClearFilters: () => void;
}

const DEFAULT_DOT_COLOR = '#4672fe';

export const NewsfeedTagFilter: React.FC<NewsfeedTagFilterProps> = memo(({
  tags,
  selectedTagIds,
  onToggleTag,
  onClearFilters,
}) => {
  const { colors } = useThemeColors();
  const isAllActive = selectedTagIds.length === 0;

  if (tags.length === 0) return null;

  const activeChipStyle = { backgroundColor: colors.newsfeed.tagActiveBg, borderColor: colors.newsfeed.tagActiveBorder };
  const inactiveChipStyle = { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder };
  const activeTextColor = { color: colors.newsfeed.tagActiveText };
  const inactiveTextColor = { color: colors.text.muted };

  return (
    <View style={styles.wrapper} accessibilityRole="toolbar" accessibilityLabel="Tag filters">
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
      >
        <TouchableOpacity
          style={[styles.chip, isAllActive ? activeChipStyle : inactiveChipStyle]}
          onPress={onClearFilters}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="All"
          accessibilityState={{ selected: isAllActive }}
        >
          <Text style={[styles.chipText, isAllActive ? activeTextColor : inactiveTextColor]}>
            All
          </Text>
        </TouchableOpacity>

        {tags.map((tag) => {
          const isSelected = selectedTagIds.includes(tag.id);
          const dotColor = tag.color || DEFAULT_DOT_COLOR;
          return (
            <TagChip
              key={tag.id}
              name={tag.name}
              dotColor={dotColor}
              isSelected={isSelected}
              onPress={() => onToggleTag(tag.id)}
              activeChipStyle={activeChipStyle}
              inactiveChipStyle={inactiveChipStyle}
              activeTextColor={activeTextColor}
              inactiveTextColor={inactiveTextColor}
            />
          );
        })}
      </ScrollView>
    </View>
  );
});

NewsfeedTagFilter.displayName = 'NewsfeedTagFilter';

const TagChip: React.FC<{
  name: string;
  dotColor: string;
  isSelected: boolean;
  onPress: () => void;
  activeChipStyle: object;
  inactiveChipStyle: object;
  activeTextColor: object;
  inactiveTextColor: object;
}> = memo(({ name, dotColor, isSelected, onPress, activeChipStyle, inactiveChipStyle, activeTextColor, inactiveTextColor }) => {
  const dynamicDot = useMemo(() => ({ backgroundColor: dotColor }), [dotColor]);

  return (
    <TouchableOpacity
      style={[styles.chip, isSelected ? activeChipStyle : inactiveChipStyle]}
      onPress={onPress}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={name}
      accessibilityState={{ selected: isSelected }}
    >
      <View style={[styles.dot, dynamicDot]} />
      <Text style={[styles.chipText, isSelected ? activeTextColor : inactiveTextColor]}>
        {name}
      </Text>
    </TouchableOpacity>
  );
});

TagChip.displayName = 'TagChip';

const styles = StyleSheet.create({
  wrapper: {
    paddingVertical: 4,
  },
  scrollContent: {
    gap: 8,
    alignItems: 'center',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 15,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
  },
  chipText: {
    ...REDESIGN_TYPOGRAPHY.tagFilter,
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
});
